import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { Flex, Select } from 'antd';
import {
  createFocusHandle,
  createOnBlur,
  FilterInputProps,
  FocusRef,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './SampleLockStateSelectFilterInput.translations.json';
import { SampleSubjectLockIcon } from './sampleSubjectLock/SampleSubjectLockIcon';

const { addTranslations, useTranslation } = i18nNsFactory(
  'sample-lock-state-select-filter-input',
);
addTranslations(translations);

export const SampleLockStateSelectFilterInput = forwardRef<
  FocusRef,
  FilterInputProps
>((props, ref) => {
  const { header, onBlur } = props;
  const { t } = useTranslation();
  const id = `${header.column.id}-filter-input`;

  useImperativeHandle(ref, () => createFocusHandle(id));
  const handleOnBlur = useMemo(
    () => createOnBlur<HTMLInputElement>(id, onBlur),
    [id, onBlur],
  );

  const options = useMemo(
    () => [
      {
        value: 'LOCKED',
        label: (
          <Flex gap="small" align="center">
            <SampleSubjectLockIcon isLocked={true} /> {t('yes')}
          </Flex>
        ),
      },
      {
        value: 'PARTIAL',
        label: (
          <Flex gap="small" align="center">
            <SampleSubjectLockIcon isLocked="some" /> {t('partially')}
          </Flex>
        ),
      },
      {
        value: 'NONE',
        label: (
          <Flex gap="small" align="center">
            <SampleSubjectLockIcon isLocked={false} /> {t('no')}
          </Flex>
        ),
      },
    ],
    [t],
  );

  const handleChange = (value?: 'LOCKED' | 'PARTIAL' | 'NONE') => {
    header.column.setFilterValue(value ?? null);
  };

  const value = header.column.getFilterValue() as
    | 'LOCKED'
    | 'PARTIAL'
    | 'NONE'
    | null;

  return (
    <Select
      id={id}
      className={id}
      placeholder={t('placeholder')}
      options={options}
      value={value === null ? undefined : value}
      onChange={handleChange}
      onBlur={handleOnBlur}
      styles={{
        popup: { root: { width: 'max-content' } },
      }}
      allowClear
      style={{ minWidth: 'max-content' }}
      defaultOpen
    />
  );
});
